import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, CloudOff, Download, CheckCircle2, AlertCircle, RefreshCw, ArrowRight, Shield, Database } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
type BackupState = 'checking' | 'found' | 'notFound' | 'restoring' | 'success' | 'error';
interface BackupCheckScreenProps {
  onContinue: () => void;
  onBack: () => void;
}
export function BackupCheckScreen({
  onContinue,
  onBack
}: BackupCheckScreenProps) {
  const {
    isLight
  } = useTheme();
  const [state, setState] = useState<BackupState>('checking');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanText, setScanText] = useState('Connecting to cloud...');
  // Simulate backup check — randomly finds or doesn't find backup
  useEffect(() => {
    if (state !== 'checking') return;
    const steps = [{
      progress: 20,
      text: 'Connecting to cloud...',
      delay: 800
    }, {
      progress: 45,
      text: 'Searching Google Drive...',
      delay: 1200
    }, {
      progress: 70,
      text: 'Scanning iCloud...',
      delay: 1000
    }, {
      progress: 90,
      text: 'Analyzing backup data...',
      delay: 800
    }, {
      progress: 100,
      text: 'Complete',
      delay: 600
    }];
    let timeout: ReturnType<typeof setTimeout>;
    let elapsed = 0;
    steps.forEach((step, i) => {
      elapsed += step.delay;
      timeout = setTimeout(() => {
        setScanProgress(step.progress);
        setScanText(step.text);
        if (i === steps.length - 1) {
          // 60% chance of finding backup for demo
          setTimeout(() => {
            setState(Math.random() > 0.4 ? 'found' : 'notFound');
          }, 500);
        }
      }, elapsed);
    });
    return () => clearTimeout(timeout);
  }, [state]);
  const handleRestore = () => {
    setState('restoring');
    // Simulate restore process
    setTimeout(() => {
      // 80% success rate for demo
      setState(Math.random() > 0.2 ? 'success' : 'error');
    }, 3000);
  };
  const handleRetry = () => {
    setScanProgress(0);
    setScanText('Connecting to cloud...');
    setState('checking');
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-435">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/15' : 'bg-blue-500/10'}`} data-id="element-436" />
      <div className={`absolute bottom-32 -left-16 w-40 h-40 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/5'}`} data-id="element-437" />

      <div className="flex-1 flex flex-col items-center justify-center relative z-10" data-id="element-438">
        <AnimatePresence mode="wait" data-id="element-439">
          {/* ─── CHECKING STATE ─── */}
          {state === 'checking' && <motion.div key="checking" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-440">
              <div className="relative flex items-center justify-center mb-8" data-id="element-441">
                {/* Pulsing outer circle */}
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`absolute w-24 h-24 rounded-full ${isLight ? 'bg-blue-500/20' : 'bg-blue-400/20'}`} data-id="element-442" />
                {/* Rotating inner ring */}
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className={`absolute w-16 h-16 rounded-full border-2 border-transparent ${isLight ? 'border-t-blue-500' : 'border-t-blue-400'}`} data-id="element-443" />
                {/* Breathing center icon */}
                <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-444">
                  <Cloud className={`w-8 h-8 ${isLight ? 'text-blue-500' : 'text-blue-400'}`} data-id="element-445" />
                </motion.div>
              </div>

              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-446">
                Checking for Backup
              </p>
              <motion.p key={scanText} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-447">
                {scanText}
              </motion.p>
            </motion.div>}

          {/* ─── BACKUP FOUND ─── */}
          {state === 'found' && <motion.div key="found" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-448">
              <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-vexu/10 border-vexu/20' : 'bg-vexu/10 border-vexu/20'}`} data-id="element-449">
                <Database className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-450" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-451">
                Backup found!
              </h1>
              <p className={`text-sm mb-8 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-452">
                We found your Vexu data in the cloud.
              </p>

              {/* Backup details card */}
              <div className={`w-full backdrop-blur-xl border rounded-2xl p-5 mb-8 ${isLight ? 'bg-white/60 border-slate-200/50 shadow-sm' : 'bg-dark-card/60 border-white/10'}`} data-id="element-453">
                <div className="space-y-3" data-id="element-454">
                  <div className="flex justify-between items-center" data-id="element-455">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-456">
                      Provider
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-457">
                      Google Drive
                    </span>
                  </div>
                  <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-458" />
                  <div className="flex justify-between items-center" data-id="element-459">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-460">
                      Last backup
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-461">
                      Mar 12, 2026
                    </span>
                  </div>
                  <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-462" />
                  <div className="flex justify-between items-center" data-id="element-463">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-464">
                      Size
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-465">
                      24.3 MB
                    </span>
                  </div>
                  <div className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-466" />
                  <div className="flex justify-between items-center" data-id="element-467">
                    <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-468">
                      Includes
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-469">
                      142 calls, 4 groups
                    </span>
                  </div>
                </div>
              </div>

              <div className="w-full space-y-3" data-id="element-470">
                <Button fullWidth size="lg" onClick={handleRestore} data-id="element-471">
                  <Download className="w-4 h-4 mr-2" data-id="element-472" />
                  Restore Backup
                </Button>
                <button onClick={onContinue} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-473">
                  Skip and start fresh
                </button>
              </div>
            </motion.div>}

          {/* ─── NOT FOUND ─── */}
          {state === 'notFound' && <motion.div key="notFound" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-474">
              <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`} data-id="element-475">
                <CloudOff className={`w-10 h-10 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-476" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-477">
                No backup found
              </h1>
              <p className={`text-sm mb-3 max-w-[280px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-478">
                We couldn't find any existing Vexu data linked to this number.
                Let's set up your account fresh.
              </p>

              <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full mb-8 ${isLight ? 'bg-slate-50' : 'bg-white/5'}`} data-id="element-479">
                <Shield className={`w-3.5 h-3.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-480" />
                <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-481">
                  You can enable backups later in Settings
                </span>
              </div>

              <div className="w-full" data-id="element-482">
                <Button fullWidth size="lg" onClick={onContinue} data-id="element-483">
                  Continue Setup
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-484" />
                </Button>
              </div>
            </motion.div>}

          {/* ─── RESTORING ─── */}
          {state === 'restoring' && <motion.div key="restoring" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-485">
              <div className="relative flex items-center justify-center mb-8" data-id="element-486">
                {/* Pulsing outer circle */}
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`absolute w-24 h-24 rounded-full ${isLight ? 'bg-vexu/20' : 'bg-vexu/20'}`} data-id="element-487" />
                {/* Rotating inner ring */}
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className={`absolute w-16 h-16 rounded-full border-2 border-transparent ${isLight ? 'border-t-vexu-600' : 'border-t-vexu'}`} data-id="element-488" />
                {/* Breathing center icon */}
                <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-489">
                  <Download className={`w-8 h-8 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-490" />
                </motion.div>
              </div>

              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-491">
                Retrieving Backup
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-492">
                Please don't close the app...
              </p>
            </motion.div>}

          {/* ─── SUCCESS ─── */}
          {state === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-493">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 15,
            delay: 0.1
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-vexu/10 border-vexu/20' : 'bg-vexu/10 border-vexu/20'}`} data-id="element-494">
                <CheckCircle2 className={`w-10 h-10 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-495" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-496">
                Restore complete!
              </h1>
              <p className={`text-sm mb-3 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-497">
                Your call history, groups, and settings have been restored
                successfully.
              </p>

              <div className={`flex flex-wrap justify-center gap-2 mb-8`} data-id="element-498">
                {['142 calls', '4 groups', 'AI settings'].map(item => <span key={item} className={`text-xs font-medium px-3 py-1.5 rounded-full ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-499">
                    {item}
                  </span>)}
              </div>

              <div className="w-full" data-id="element-500">
                <Button fullWidth size="lg" onClick={onContinue} data-id="element-501">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-502" />
                </Button>
              </div>
            </motion.div>}

          {/* ─── ERROR ─── */}
          {state === 'error' && <motion.div key="error" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex flex-col items-center text-center w-full" data-id="element-503">
              <motion.div initial={{
            scale: 0.5
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-8 border ${isLight ? 'bg-red-50 border-red-100' : 'bg-red-500/10 border-red-500/20'}`} data-id="element-504">
                <AlertCircle className={`w-10 h-10 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-505" />
              </motion.div>

              <h1 className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-506">
                Restore failed
              </h1>
              <p className={`text-sm mb-8 max-w-[260px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-507">
                Something went wrong while restoring your data. Please check
                your connection and try again.
              </p>

              <div className="w-full space-y-3" data-id="element-508">
                <Button fullWidth size="lg" onClick={handleRetry} data-id="element-509">
                  <RefreshCw className="w-4 h-4 mr-2" data-id="element-510" />
                  Try Again
                </Button>
                <button onClick={onContinue} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-511">
                  Skip and start fresh
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}